import {
  type ComponentPropsWithRef,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import { CenterFlex, Flex } from '@/components/base';
import useCombinedRefs from '@/hooks/pure/useCombinedRefs';
import { radius } from '@/styles/sizes';
import keymap from '@/utils/keymap';

import { FontSize, HeightSize, type InputT } from './types';

const Input = (
  props: ComponentPropsWithRef<'input'> & InputT,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const {
    leftIcon,
    rightIcon,
    sizeType = 's',
    initFocus = false,
    hideBorder = false,
    style,
    useBlur = true,
    onEnter,
    onEscape,
    ...rest
  } = props;
  const localRef = useRef(null) as RefObject<HTMLInputElement>;
  const combinedRef = useCombinedRefs(ref, localRef);

  const handleInputKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;
    props.onKeyDown?.(e);
    if (keymap.isEscape(e)) onEscape?.();
    if (keymap.isEnter(e)) onEnter?.(combinedRef?.current?.value || '', e);
    if (useBlur && (keymap.isEscape(e) || keymap.isEnter(e))) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  useEffect(() => {
    if (localRef.current) {
      if (initFocus) {
        localRef.current.focus();
      }
    }
  }, [initFocus]);

  // Enter 나 ESC 버튼 누르면 blur 아웃 처리
  return (
    <SInputWrapper
      sizeType={sizeType}
      hasLeftIcon={!!leftIcon}
      hasRightIcon={!!rightIcon}
      style={props.wrapperStyle}
    >
      {leftIcon && (
        <CenterFlex className="absolute left-2.5 z-1">{leftIcon}</CenterFlex>
      )}

      <SInput
        {...rest}
        hasLeftIcon={!!leftIcon}
        hideBorder={hideBorder}
        sizeType={sizeType}
        style={{ ...style }}
        onKeyDown={handleInputKeydown}
        ref={combinedRef}
      />

      {rightIcon && (
        <CenterFlex className="absolute right-2.5 z-1">{rightIcon}</CenterFlex>
      )}
    </SInputWrapper>
  );
};

type StyledInputProps = {
  hideBorder?: boolean;
  sizeType: SizeType;
  hasLeftIcon?: boolean;
  hasRightIcon?: boolean;
  css?: StyledCss;
};

const SInputWrapper = styled(Flex)<StyledInputProps>`
  width: 100%;
  align-items: center;
  position: relative;
  font-size: ${({ sizeType }) => `${FontSize[sizeType]}px`};
`;

const SInput = styled.input<StyledInputProps>`
  width: 100%;
  min-width: 140px;
  font-size: ${({ sizeType }) => `${FontSize[sizeType]}px`};
  height: ${({ sizeType }) => `${HeightSize[sizeType]}px`};
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: ${({ hasLeftIcon }) => (hasLeftIcon ? '32px' : '12px')};
  padding-right: ${({ hasRightIcon }) => (hasRightIcon ? '32px' : '12px')};
  border-radius: ${radius.input}px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background-color: ${({ theme }) => theme.colors.gray100};
  outline: none;
  color: ${(props) => props.theme.colors.gray800};
  transition: all 0.2s;

  ::placeholder {
    color: ${(props) => props.theme.colors.gray500};
  }

  :hover {
    filter: brightness(0.98);
  }

  :focus {
    filter: brightness(1);
    :hover {
      filter: brightness(0.96);
    }
  }

  :disabled {
    background-color: ${(props) => props.theme.colors.gray300};
    border: none;
    color: ${(props) => props.theme.colors.gray500};
  }

  ${({ hideBorder }) =>
    hideBorder &&
    css`
      border-color: transparent;
      :hover,
      :focus {
        border-color: transparent;
      }
    `}

  ${(props) => props.css && props.css};
`;

export default forwardRef(Input);
